import {
  Table,
  Box,
  Button,
  ButtonGroup,
  HStack,
  Input,
  Menu,
  Text,
  VStack,
  Portal,
} from '@chakra-ui/react';
import { FiChevronDown } from 'react-icons/fi'
import { StatusChip } from '../../shared'
import { useIsMobile } from '../../../hooks/useIsMobile'
import type { AskPosition, AskResponse, LegislatorVote, LegislatorVoteRowDto, SentimentDisposition, VoteSource } from '../../../types/lobbyDay'
import { dispositionFor } from './reconciliationLogic'
import { DispositionChip, DISPOSITION_META } from './DispositionChip'

// DISPOSITION_META's label carries a leading emoji glyph meant for the chip; strip it for the
// "Force …" menu item so the menu text reads as plain words next to the verb.
const dispositionWords = (d: SentimentDisposition) => DISPOSITION_META[d].label.replace(/^\S+\s*/, '')

const VOTE_OPTIONS: { value: LegislatorVote; label: string }[] = [
  { value: 'Yea', label: 'Yea' },
  { value: 'Nay', label: 'Nay' },
  { value: 'DidNotVote', label: "Didn't vote" },
]
const OVERRIDE_OPTIONS: SentimentDisposition[] = ['Excited', 'Jilted', 'Betrayed', 'Pending']
const PROMISE_LABEL: Record<AskResponse, string> = { Yes: 'Yes', No: 'No', Maybe: 'Maybe', NotRecorded: '—' }

export interface VoteRowProps {
  row: LegislatorVoteRowDto
  position: AskPosition
  // Carries the full triple every time -- a vote/override click passes the row's current notes
  // unchanged, and a notes blur passes the row's current vote/override unchanged. That lets a
  // single handler seed a batched edit entry (ReconciliationTab.onRowChange) regardless of which
  // field the user actually touched, so notes rides the same "Save votes" batch save as vote and
  // override edits rather than saving independently on blur.
  onChange: (vote: LegislatorVote, override: SentimentDisposition | null, notes: string | null) => void
  // Task 10: a humanized "why no vote" reason for an unresolved legislator, mapped from the last
  // LegiScan fill's misses by AskReconciliationSection. Undefined when no fill has run (or the row
  // was resolved) -- the "no vote found" hint still shows, just without a reason line.
  missReason?: string
}

// Task 10: provenance of the recorded vote. LegiScan-sourced -> an info chip; a hand-set Manual vote
// -> a neutral chip + a lock glyph (UI-only; the real Manual lock is enforced backend-side by
// SaveVotesAsync's VoteSource stamp + the fill's skip). NotRecorded shows nothing.
function VoteSourceBadge({ voteSource }: { voteSource: VoteSource }) {
  if (voteSource === 'LegiScan') return <StatusChip variant="info">LegiScan</StatusChip>
  if (voteSource === 'Manual') {
    return (
      <HStack gap={1}>
        <StatusChip variant="neutral">Manual</StatusChip>
        <Text fontSize="xs" color="muted" aria-label="Locked">🔒</Text>
      </HStack>
    )
  }
  return null
}

function NoVoteFound({ missReason }: { missReason?: string }) {
  return (
    <Box>
      <Text fontSize="xs" color="muted" fontStyle="italic">no vote found</Text>
      {!!missReason && <Text fontSize="xs" color="warning.fg">{missReason}</Text>}
    </Box>
  )
}

function liveDisposition(row: LegislatorVoteRowDto, position: AskPosition): SentimentDisposition {
  return dispositionFor(row.promise, row.vote, row.dispositionOverride, position)
}

function VoteButtons({ row, onChange }: VoteRowProps) {
  return (
    <ButtonGroup size="sm" attached variant="outline">
      {VOTE_OPTIONS.map((o) => (
        <Button key={o.value} minH="44px"
          colorPalette={row.vote === o.value ? 'blue' : 'gray'}
          variant={row.vote === o.value ? 'solid' : 'outline'}
          onClick={() => onChange(o.value, row.dispositionOverride, row.notes)}>
          {o.label}
        </Button>
      ))}
    </ButtonGroup>
  );
}

function OverrideMenu({ row, position, onChange }: VoteRowProps) {
  return (
    <Menu.Root>
      <Menu.Trigger asChild><Button size="sm" minH="44px" variant="ghost">
          <DispositionChip disposition={liveDisposition(row, position)} />
          <FiChevronDown /></Button></Menu.Trigger>
      <Portal><Menu.Positioner><Menu.Content>
            <Menu.Item onSelect={() => onChange(row.vote, null, row.notes)} value='item-0'>Auto (clear override)</Menu.Item>
            {OVERRIDE_OPTIONS.map((d) => (
              <Menu.Item key={d} onSelect={() => onChange(row.vote, d, row.notes)} value={d}>Force {dispositionWords(d)}</Menu.Item>
            ))}
          </Menu.Content></Menu.Positioner></Portal>
    </Menu.Root>
  );
}

function NotesInput({ row, onChange }: VoteRowProps) {
  // Chakra v3 `size="xs"` resolves to a 32px control -- below the ≥44px touch-target floor.
  // Mobile renders this inside a card (see below); desktop renders it in a dense table cell,
  // where the plan's original "xs" density is fine since a mouse pointer has no minimum target
  // size. Branch on the same useIsMobile() signal the row itself already uses, rather than
  // bumping the desktop table row height for a rule that only applies to touch.
  const isMobile = useIsMobile()
  return (
    <Input
      size={isMobile ? 'sm' : 'xs'}
      minH={isMobile ? '44px' : undefined}
      aria-label={`Why the vote diverged for ${row.legislatorName}`}
      placeholder="Why did it change?"
      defaultValue={row.notes ?? ''}
      onBlur={(e) => {
        if (e.target.value !== (row.notes ?? '')) onChange(row.vote, row.dispositionOverride, e.target.value || null)
      }}
    />
  );
}

export function LegislatorVoteRow(props: VoteRowProps) {
  const { row, missReason } = props
  const isMobile = useIsMobile()

  const promise = (
    <Text fontSize="sm">
      {row.promiseMixed ? 'Mixed' : PROMISE_LABEL[row.promise]}
    </Text>
  )

  const noVote = row.vote === 'NotRecorded'
  const provenance = (
    <HStack gap={2} flexWrap="wrap">
      {noVote ? <NoVoteFound missReason={missReason} /> : <VoteSourceBadge voteSource={row.voteSource} />}
    </HStack>
  )

  if (isMobile) {
    return (
      <Box bg="surface" p={4} borderRadius="lg" shadow="sm">
        <HStack justify="space-between">
          <Box>
            <Text fontWeight="semibold">{row.legislatorName}{row.party ? ` (${row.party[0]})` : ''}</Text>
            <Text fontSize="xs" color="gray.500" lineClamp={1}>{row.district}</Text>
          </Box>
          <VStack gap={0} align="end">
            <Text fontSize="xs" color="gray.500">Promise</Text>
            {promise}
          </VStack>
        </HStack>
        <HStack mt={3} justify="space-between" flexWrap="wrap" gap={2}>
          <VoteButtons {...props} />
          <OverrideMenu {...props} />
        </HStack>
        <Box mt={2}>{provenance}</Box>
        <Text fontSize="xs" color="gray.500" mt={2}>{row.attendeeCount} attendee(s)</Text>
        <NotesInput {...props} />
      </Box>
    );
  }

  return (
    <Table.Row>
      <Table.Cell>
        <Text fontWeight="semibold">{row.legislatorName}{row.party ? ` (${row.party[0]})` : ''}</Text>
        <Text fontSize="xs" color="gray.500" lineClamp={1}>{row.district}</Text>
      </Table.Cell>
      <Table.Cell>{promise}</Table.Cell>
      <Table.Cell>
        <VStack align="start" gap={1}>
          <VoteButtons {...props} />
          {provenance}
        </VStack>
      </Table.Cell>
      <Table.Cell><OverrideMenu {...props} /></Table.Cell>
      <Table.Cell textAlign="end">{row.attendeeCount}</Table.Cell>
      <Table.Cell><NotesInput {...props} /></Table.Cell>
    </Table.Row>
  );
}
